import { BrowserRouter, Outlet, Route, Routes as RoutesDOM } from 'react-router-dom'
import { lazy, Suspense } from 'react'

import AuthProvider from '@stores/auth.context/auth.context'
import NotificationProvider from '@stores/notification.context/notification.context'
import OrdersProposalProvider from '@stores/orders-proposal.context'
import OrdersListLayoutProvider from '@stores/orders/orders-list-layout.context'

import DrawerLayout from '@layouts/drawer-layout/drawer-layout'
import { PrivateRoute } from './private-route'
import CircularProgress from '@components/CircularProgress/CircularProgress'

// Lazy imports
const AddressInfo = lazy(() => import('@pages/create-account/AddressInfo'))
const ContactInfo = lazy(() => import('@pages/create-account/ContactInfo'))
const CreateAccountLayout = lazy(() => import('@pages/create-account/CreateAccount'))
const Dashboard = lazy(() => import('@pages/dashboard'))
const Explanation = lazy(() => import('@pages/create-account/Explanation'))
const ForgotPassword = lazy(() => import('@pages/forgot-password'))
const HubInfo = lazy(() => import('@pages/create-account/HubInfo'))
const LoginInfo = lazy(() => import('@pages/create-account/login-info'))
const Login = lazy(() => import('@pages/login'))
const OrderDetailsLost = lazy(() => import('@pages/order-details-lost'))
const OrderDetails = lazy(() => import('@pages/order-details'))
const OrdersListLayout = lazy(() => import('@pages/order-list'))
const ProductListPage = lazy(() => import('@pages/product-list'))
const ResetPassword = lazy(() => import('@pages/reset-password'))
const Welcome = lazy(() => import('@pages/create-account/Welcome'))
const OrdersToSeparate = lazy(() => import('@pages/order-list/to-separate'))
const BasicInfo = lazy(() => import('@pages/account/basic-info'))
const AccountLayout = lazy(() => import('@pages/account'))
const Documents = lazy(() => import('@pages/account/documents'))
const Users = lazy(() => import('@pages/account/users'))
const Finance = lazy(() => import('@pages/finance'))
const Terms = lazy(() => import('@pages/terms/terms'))
const OrdersToDropOff = lazy(() => import('@pages/order-list/to-dropoff'))
const OrdersInTransit = lazy(() => import('@pages/order-list/in-transit'))
const OrdersToPickup = lazy(() => import('@pages/order-list/to-pickup'))
const OrdersCompleted = lazy(() => import('@pages/order-list/completed'))
const OrdersLost = lazy(() => import('@pages/order-list/lost'))
const OrdersTutorial = lazy(() => import('@pages/orders-tutorial/orders-tutorial'))

const SuspenseLayout = () => (
  <Suspense
    fallback={
      <div className='flex justify-center items-center h-screen'>
        <CircularProgress className='h-8 w-8' color='primary' />
      </div>
    }>
    <Outlet />
  </Suspense>
)

export const Routes = () => {
  return (
    <BrowserRouter>
      <RoutesDOM>
        <Route element={<SuspenseLayout />}>
          <Route path='/entrar' element={<Login />} />
          <Route path='/redefinir-senha/:token' element={<ResetPassword />} />
          <Route path='/esqueci-minha-senha' element={<ForgotPassword />} />

          <Route element={<SuspenseLayout />}>
            <Route path='/criar-conta' element={<CreateAccountLayout />}>
              <Route path='/criar-conta/:step/bem-vindo' element={<Welcome />} />
              <Route path='/criar-conta/:step/explicacao' element={<Explanation />} />
              <Route path='/criar-conta/:step/login' element={<LoginInfo />} />
              <Route element={<PrivateRoute redirect='/criar-conta/1/bem-vindo' />}>
                <Route element={<AuthProvider />}>
                  <Route path='/criar-conta/:step/loja' element={<HubInfo />} />
                  <Route path='/criar-conta/:step/endereco' element={<AddressInfo />} />
                  <Route path='/criar-conta/:step/contato' element={<ContactInfo />} />
                </Route>
              </Route>
            </Route>
          </Route>

          <Route element={<PrivateRoute />}>
            <Route element={<AuthProvider />}>
              <Route element={<NotificationProvider />}>
                <Route element={<OrdersListLayoutProvider />}>
                  <Route element={<OrdersProposalProvider />}>
                    <Route path='/pedidos/tutorial' element={<OrdersTutorial />} />
                    <Route element={<DrawerLayout />}>
                      <Route element={<SuspenseLayout />}>
                        <Route path='/desempenho' element={<Dashboard />} />

                        <Route element={<OrdersListLayout />}>
                          <Route index element={<OrdersToSeparate />} />
                          <Route path='/pedidos' element={<OrdersToSeparate />} />
                          <Route path='/pedidos/para-despacho' element={<OrdersToDropOff />} />
                          <Route path='/pedidos/para-coleta' element={<OrdersToPickup />} />
                          <Route path='/pedidos/em-transito' element={<OrdersInTransit />} />
                          <Route path='/pedidos/concluidos' element={<OrdersCompleted />} />
                          <Route path='/pedidos/perdidos' element={<OrdersLost />} />
                        </Route>

                        <Route path='/pedidos/:order_uuid' element={<OrderDetails />} />
                        <Route
                          path='/pedidos/:order_uuid_lost/perdido'
                          element={<OrderDetailsLost />}
                        />
                        <Route path='/produtos' element={<ProductListPage />} />

                        <Route path='/financeiro' element={<Finance />} />
                        <Route path='/financeiro/:uuid' element={<Finance />} />

                        <Route path='/conta' element={<AccountLayout />}>
                          <Route index element={<BasicInfo />} />
                          <Route path='/conta/documentos' element={<Documents />} />
                          <Route path='/conta/usuarios' element={<Users />} />
                        </Route>
                      </Route>
                    </Route>
                  </Route>
                </Route>
              </Route>
            </Route>
          </Route>
          <Route path='/:uuid/termos' element={<Terms singlePage />} />
        </Route>
      </RoutesDOM>
    </BrowserRouter>
  )
}
